import { PhotoMetadataRecord } from "../catalog"
import type { RawInfo } from "./fastlib"
import { parseJsonDCP, type DCPProfile } from "@/core/dcp/camera-profile"
import type { MetadataResult } from "@/lib/metadata/core/types"
import { DNGColorWhiteBalance } from "@/io/formats/raw/white-balance/dng-white-balance"
import { RAWColorWhiteBalance } from "@/io/formats/raw/white-balance/raw-white-balance"
import { GenericWhiteBalance } from "@/io/formats/raw/white-balance/generic-white-balance"
import { rationalsToFloats, resolveColorMatrix } from "@/utils/exif"
import type { mat3 } from "gl-matrix"

const appleDeviceIdMap = {
	"iPhone 6s": "iPhone8,1",
	"iPhone 6s Plus": "iPhone8,2",
	"iPhone SE (GSM)": "iPhone8,4",
	"iPhone 7": "iPhone9,1",
	"iPhone 7 Plus": "iPhone9,2",
	"iPhone 8": "iPhone10,1",
	"iPhone 8 Plus": "iPhone10,2",
	"iPhone X Global": "iPhone10,3",
	"iPhone X GSM": "iPhone10,6",
	"iPhone XS": "iPhone11,2",
	"iPhone XS Max": "iPhone11,4",
	"iPhone XS Max Global": "iPhone11,6",
	"iPhone XR": "iPhone11,8",
	"iPhone 11": "iPhone12,1",
	"iPhone 11 Pro": "iPhone12,3",
	"iPhone 11 Pro Max": "iPhone12,5",
	"iPhone SE 2nd Gen": "iPhone12,8",
	"iPhone 12 Mini": "iPhone13,1",
	"iPhone 12": "iPhone13,2",
	"iPhone 12 Pro": "iPhone13,3",
	"iPhone 12 Pro Max": "iPhone13,4",
	"iPhone 13 Pro": "iPhone14,2",
	"iPhone 13 Pro Max": "iPhone14,3",
	"iPhone 13 Mini": "iPhone14,4",
	"iPhone 13": "iPhone14,5",
	"iPhone SE 3rd Gen": "iPhone14,6",
	"iPhone 14": "iPhone14,7",
	"iPhone 14 Plus": "iPhone14,8",
	"iPhone 14 Pro": "iPhone15,2",
	"iPhone 14 Pro Max": "iPhone15,3",
	"iPhone 15": "iPhone15,4",
	"iPhone 15 Plus": "iPhone15,5",
	"iPhone 15 Pro": "iPhone16,1",
	"iPhone 15 Pro Max": "iPhone16,2",
	"iPad mini 4 (WiFi)": "iPad5,1",
	"4th Gen iPad mini (WiFi+Cellular)": "iPad5,2",
	"iPad Air 2 (WiFi)": "iPad5,3",
	"iPad Air 2 (Cellular)": "iPad5,4",
	"iPad Pro (9.7 inch, WiFi)": "iPad6,3",
	"iPad Pro (9.7 inch, WiFi+LTE)": "iPad6,4",
	"iPad Pro (12.9 inch, WiFi)": "iPad6,7",
	"iPad Pro (12.9 inch, WiFi+LTE)": "iPad6,8",
	"iPad (2017)": "iPad6,11",
	"iPad Pro 2nd Gen (WiFi)": "iPad7,1",
	"iPad Pro 2nd Gen (WiFi+Cellular)": "iPad7,2",
	"iPad Pro 10.5-inch 2nd Gen": "iPad7,3",
	"iPad 6th Gen (WiFi)": "iPad7,5",
	"iPad 6th Gen (WiFi+Cellular)": "iPad7,6",
	"iPad 7th Gen 10.2-inch (WiFi)": "iPad7,11",
	"iPad 7th Gen 10.2-inch (WiFi+Cellular)": "iPad7,12",
	"iPad Pro 11 inch 3rd Gen (WiFi)": "iPad8,1",
	"iPad Pro 11 inch 3rd Gen (1TB, WiFi)": "iPad8,2",
	"iPad Pro 11 inch 3rd Gen (WiFi+Cellular)": "iPad8,3",
	"iPad Pro 11 inch 3rd Gen (1TB, WiFi+Cellular)": "iPad8,4",
	"iPad Pro 12.9 inch 3rd Gen (WiFi)": "iPad8,5",
	"iPad Pro 12.9 inch 3rd Gen (1TB, WiFi)": "iPad8,6",
	"iPad Pro 12.9 inch 3rd Gen (WiFi+Cellular)": "iPad8,7",
	"iPad Pro 12.9 inch 3rd Gen (1TB, WiFi+Cellular)": "iPad8,8",
	"iPad Pro 11 inch 4th Gen (WiFi)": "iPad8,9",
	"iPad Pro 11 inch 4th Gen (WiFi+Cellular)": "iPad8,10",
	"iPad Pro 12.9 inch 4th Gen (WiFi)": "iPad8,11",
	"iPad Pro 12.9 inch 4th Gen (WiFi+Cellular)": "iPad8,12",
	"iPad mini 5th Gen (WiFi)": "iPad11,1",
	"iPad mini 5th Gen": "iPad11,2",
	"iPad Air 3rd Gen (WiFi)": "iPad11,3",
	"iPad Air 3rd Gen": "iPad11,4",
	"iPad 8th Gen (WiFi)": "iPad11,6",
	"iPad 8th Gen (WiFi+Cellular)": "iPad11,7",
	"iPad 9th Gen (WiFi)": "iPad12,1",
	"iPad 9th Gen (WiFi+Cellular)": "iPad12,2",
	"iPad mini 6th Gen (WiFi)": "iPad14,1",
	"iPad mini 6th Gen (WiFi+Cellular)": "iPad14,2",
	"iPad Air 4th Gen (WiFi)": "iPad13,1",
	"iPad Air 4th Gen (WiFi+Cellular)": "iPad13,2",
	"iPad Pro 11 inch 5th Gen": "iPad13,4",
	"iPad Pro 12.9 inch 5th Gen": "iPad13,8",
	"iPad Air 5th Gen (WiFi)": "iPad13,16",
	"iPad Air 5th Gen (WiFi+Cellular)": "iPad13,17",
	"iPad 10th Gen": "iPad13,18",
	"iPad Pro 11 inch 4th Gen": "iPad14,3",
	"iPad Pro 12.9 inch 6th Gen": "iPad14,5",
}

// Some camera_model decoded from rawspeed doesn't match the name in camera profile file
const cameraModelMap: Record<string, string> = {
	"Canon EOS R5m2": "Canon EOS R5 Mark II",
	"Canon EOS R6m2": "Canon EOS R6 Mark II",
	GFX100S: "GFX 100S",
}

export function getAppleCameraID(cameraModel: string, lensModel: string | null | undefined) {
	const deviceId: string = appleDeviceIdMap[cameraModel] ?? cameraModel
	const lens = lensModel

	if (lens?.includes("front")) {
		return deviceId + " front camera"
	}

	if (lens?.includes("back")) {
		if (lens.includes("telephoto")) {
			return deviceId + " back telephoto camera"
		} else if (lens.includes("ultra wide")) {
			return deviceId + " back ultra wide camera"
		} else {
			return deviceId + " back camera"
		}
	}

	return deviceId
}

export function getCameraModel(cameraModel: string) {
	return cameraModelMap[cameraModel] ?? cameraModel
}

function formatCameraProfileId(
	cameraMake: string | null | undefined,
	cameraModel: string | null | undefined,
	lensModel: string | null | undefined,
) {
	const model = cameraModel?.trim()

	if (!model) {
		return undefined
	}

	const cameraMaker = cameraMake?.trim().split(" ")[0] ?? ""

	if (cameraMaker === "Apple") {
		return getAppleCameraID(model, lensModel)
	}

	let profileModel = getCameraModel(model)

	if (!cameraMaker) {
		return profileModel
	}

	// Strip redundant make prefix from model (case-insensitive).
	// EXIF Model often includes the brand: Make="SONY" Model="Sony ILCE-7M4"
	// We want "SONY ILCE-7M4", not "SONY Sony ILCE-7M4".
	const firstWord = profileModel.split(" ")[0]
	if (
		firstWord &&
		firstWord.toUpperCase() === cameraMaker.toUpperCase() &&
		profileModel.length > firstWord.length
	) {
		profileModel = profileModel.slice(firstWord.length).trimStart()
	}

	return profileModel.toUpperCase().startsWith(cameraMaker.toUpperCase())
		? profileModel
		: `${cameraMaker} ${profileModel}`
}

// --- Camera Profile ID & Loading ---

export function getCameraProfileId(
	info: Pick<RawInfo, "camera_maker" | "camera_model">,
	metadata: PhotoMetadataRecord | undefined,
): string {
	return (
		formatCameraProfileId(info.camera_maker, info.camera_model, metadata?.lensModel) ??
		formatCameraProfileId(metadata?.cameraMake, metadata?.cameraModel, metadata?.lensModel) ??
		""
	)
}

export function getCameraProfileIdFromExtractedMetadata(
	metadata: Pick<MetadataResult, "easyaccess">,
): string | undefined {
	return formatCameraProfileId(
		metadata.easyaccess.Make,
		metadata.easyaccess.Model,
		metadata.easyaccess.LensName,
	)
}

const profileCache = new Map<string, DCPProfile | undefined>()

export async function loadCameraProfile(cameraID: string): Promise<DCPProfile | undefined> {
	if (profileCache.has(cameraID)) {
		return profileCache.get(cameraID)
	}

	try {
		const packagedCameraProfiles = import.meta.glob<string>("@assets/camera-profiles/*.json", {
			query: "?url",
			import: "default",
			eager: false,
		})

		const loader =
			packagedCameraProfiles[`/assets/camera-profiles/${cameraID.toUpperCase()}.json`]
		if (!loader) {
			profileCache.set(cameraID, undefined)
			return undefined
		}
		const url = await loader()

		const res = await fetch(url)
		if (!res.ok) {
			profileCache.set(cameraID, undefined)
			return undefined
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		const profile = parseJsonDCP(await res.json())
		profileCache.set(cameraID, profile)
		return profile
	} catch (e) {
		console.error(`Could not find matching DCP profile for ${cameraID}`, e)
		profileCache.set(cameraID, undefined)
		return undefined
	}
}

// --- WB Profile Bundle ---

export interface WBProfileEntry {
	CalibrationIlluminant1?: number
	CalibrationIlluminant2?: number
	ColorMatrix1?: number[]
	ColorMatrix2?: number[]
	ForwardMatrix1?: number[]
	ForwardMatrix2?: number[]
	FallbackColorMatrix?: number[]
}

export let wbProfilesCache: Record<string, WBProfileEntry> | null = null
let wbProfilesPromise: Promise<Record<string, WBProfileEntry>> | null = null

export async function preloadWBProfiles(): Promise<void> {
	if (wbProfilesCache) return
	if (wbProfilesPromise) {
		await wbProfilesPromise
		return
	}

	wbProfilesPromise = (async () => {
		const profiles: Record<string, string> = import.meta.glob(
			"@assets/metadata/wb-profiles.json",
			{
				query: "?url",
				import: "default",
				eager: true,
			},
		)
		const url = profiles["/assets/metadata/wb-profiles.json"]

		if (!url) {
			throw new Error("wb-profiles.json not found")
		}

		const res = await fetch(url)
		const data: Record<string, WBProfileEntry> = await res.json()
		wbProfilesCache = data
		return data
	})()

	await wbProfilesPromise
}

export function getWBProfile(cameraId: string): WBProfileEntry | undefined {
	return wbProfilesCache?.[cameraId.toUpperCase()]
}

/**
 * Compute white balance (temperature + tint) from extracted EXIF/MakerNote metadata,
 * without requiring rawspeed. Mirrors the rawspeed WB pipeline:
 *
 * 1. Resolve camera ID → load WB profile (DCP bundle with dual-illuminant matrices)
 * 2. Determine path: DNG (AsShotNeutral + ColorMatrix1/2) vs RAW (maker-note WB coefficients)
 * 3. Construct a minimal RawInfo from EXIF tags + WB profile, then run the same
 *    DNGColorWhiteBalance / RAWColorWhiteBalance classes used by the rawspeed path
 * 4. Return {temperature, tint}, or undefined if insufficient data (e.g. JPEGs)
 *
 * @author Claude
 */
export async function getWBFromExtractedMetadata(
	metadata: MetadataResult,
	options?: { skipProfileLookup?: boolean },
): Promise<GenericWhiteBalance | undefined> {
	if (!options?.skipProfileLookup) {
		await preloadWBProfiles()
	}
	const cameraId = getCameraProfileIdFromExtractedMetadata(metadata)
	const wbProfile = !options?.skipProfileLookup && cameraId ? getWBProfile(cameraId) : undefined
	const cc = metadata.colorCorrectionsExif
	const profileName = cc.ProfileName ?? ""

	const shouldUseDNG =
		profileName === "Apple Embedded Color Profile" || profileName === "Embedded"

	// Guard: need at least WB coefficients + a color matrix, or DNG AsShotNeutral + ColorMatrix1.
	// Without these (e.g. JPEG files) the WB computation produces garbage.
	const hasWBCoeffs =
		(Array.isArray(cc.whiteBalanceCoefficients) && cc.whiteBalanceCoefficients.length > 0) ||
		(Array.isArray(cc.FujiWBGRBLevels) && cc.FujiWBGRBLevels.length >= 3)
	const hasAsShotNeutral = !!cc.AsShotNeutral && cc.AsShotNeutral.length >= 3
	const hasColorMatrix = !!cc.ColorMatrix1 || !!wbProfile

	if (!hasColorMatrix || (!hasWBCoeffs && !hasAsShotNeutral)) {
		return undefined
	}

	// Build DCPProfile for RAW path from DCP bundle only (matches rawspeed's loadCameraProfile)
	const dcpProfile: DCPProfile = {
		calibrationTemperature1: wbProfile?.CalibrationIlluminant1,
		calibrationTemperature2: wbProfile?.CalibrationIlluminant2,
		colorMatrix1: wbProfile?.ColorMatrix1
			? (new Float32Array(wbProfile.ColorMatrix1) as unknown as mat3)
			: undefined,
		colorMatrix2: wbProfile?.ColorMatrix2
			? (new Float32Array(wbProfile.ColorMatrix2) as unknown as mat3)
			: undefined,
		forwardMatrix1: wbProfile?.ForwardMatrix1
			? (new Float32Array(wbProfile.ForwardMatrix1) as unknown as mat3)
			: undefined,
		forwardMatrix2: wbProfile?.ForwardMatrix2
			? (new Float32Array(wbProfile.ForwardMatrix2) as unknown as mat3)
			: undefined,
	}

	// Fujifilm CFA TIFF stores WB as G,R,B in tag 0xF00E — reorder to R,G,B
	if (
		!cc.whiteBalanceCoefficients &&
		Array.isArray(cc.FujiWBGRBLevels) &&
		cc.FujiWBGRBLevels.length >= 3
	) {
		const g = cc.FujiWBGRBLevels[0] ?? 0
		const r = cc.FujiWBGRBLevels[1] ?? 0
		const b = cc.FujiWBGRBLevels[2] ?? 0
		if (r > 0 && g > 0 && b > 0) {
			cc.whiteBalanceCoefficients = [r, g, b]
		}
	}

	// Construct minimal RawInfo with fields needed by WB classes
	const info = {
		// DNG path: illuminants (EXIF enum values, converted by DNGColorWhiteBalance)
		calibration_illuminant1: cc.CalibrationIlluminant1 ?? 0,
		calibration_illuminant2: cc.CalibrationIlluminant2 ?? 0,

		// Color matrices: embedded EXIF → WB bundle
		color_mat1: resolveColorMatrix(cc.ColorMatrix1, wbProfile?.ColorMatrix1),
		color_mat2: resolveColorMatrix(cc.ColorMatrix2, wbProfile?.ColorMatrix2),

		// Reduction / calibration matrices from EXIF
		reduction_mat1: cc.ReductionMatrix1
			? rationalsToFloats(cc.ReductionMatrix1)
			: new Float32Array(9),
		reduction_mat2: cc.ReductionMatrix2
			? rationalsToFloats(cc.ReductionMatrix2)
			: new Float32Array(9),
		camera_calibration1: cc.CameraCalibration1
			? rationalsToFloats(cc.CameraCalibration1)
			: new Float32Array(0),
		camera_calibration2: cc.CameraCalibration2
			? rationalsToFloats(cc.CameraCalibration2)
			: new Float32Array(0),

		// Analog balance / as-shot neutral from EXIF
		analog_balance: cc.AnalogBalance
			? rationalsToFloats(cc.AnalogBalance)
			: new Float32Array(0),
		as_shot_neutral: cc.AsShotNeutral ? rationalsToFloats(cc.AsShotNeutral) : undefined,

		// RAW path: WB coefficients from maker notes, or derived from AsShotNeutral (1/neutral)
		wb_coeffs:
			Array.isArray(cc.whiteBalanceCoefficients) && cc.whiteBalanceCoefficients.length > 0
				? new Float32Array([...cc.whiteBalanceCoefficients, 0])
				: cc.AsShotNeutral && cc.AsShotNeutral.length >= 3
					? (() => {
							const n = rationalsToFloats(cc.AsShotNeutral)
							return new Float32Array([
								1 / (n[0] ?? 1),
								1 / (n[1] ?? 1),
								1 / (n[2] ?? 1),
								0,
							])
						})()
					: new Float32Array([1, 1, 1, 0]),

		// RAW path fallback color matrix: prefer D65 (ColorMatrix2) to match rawspeed's database
		color_matrix: cc.ColorMatrix2
			? rationalsToFloats(cc.ColorMatrix2)
			: resolveColorMatrix(cc.ColorMatrix1, wbProfile?.FallbackColorMatrix),

		// DCP profile for RAW path
		profile: dcpProfile,

		profile_name: profileName,
		camera_maker: metadata.easyaccess.Make ?? "",
		camera_model: metadata.easyaccess.Model ?? "",
	} as unknown as RawInfo

	const wb = shouldUseDNG ? new DNGColorWhiteBalance(info) : new RAWColorWhiteBalance(info)

	return wb
}
